// Central client state + a minimal pub/sub. This directly replaces the
// shadow-session-state "which widget just changed" arbitration hack from
// viz/app.py (~lines 1082-1146) — that only existed because Streamlit has no
// concept of "which widget was just interacted with" (every rerun looks the
// same regardless of trigger). Here, a click handler calls setState(...)
// synchronously and directly; there's no rerun-everything step, so there's no
// ambiguity to resolve: whichever handler fires last (map click, browse-table
// row, member-table row) simply wins, which is exactly "most recent action".
import type { HullFeatureCollection } from "./api";
import type {
  AreaPackageMiniConfig,
  AreaPackagePreviewResult,
  ColumnInfo,
  ColumnKind,
  GeometryResponse,
  Legend,
  NearbyParcel,
  PackageBrowseRow,
  PackageReadiness,
  PipesResponse,
  ReferenceLayerCatalogEntry,
  ReferenceLayerGeoJSON,
  WaterType,
} from "./types";

// A pipe passes an "above"/"below" numeric filter or an "is" categorical/
// boolean filter — kept this small (no "between"/"not equal"/multi-select)
// to match what was actually asked for; add operators here if that grows.
export type FilterOperator = "gte" | "lte" | "eq";

/** One "column filter" chip — pick a column, then a threshold (numeric),
 * a value (categorical), or true/false (boolean). Applied client-side in
 * map.ts:joinPipes, stacked (AND) on top of the existing package-visibility
 * filters — never a replacement for them. */
export interface ColumnFilter {
  id: string;
  column: string;
  kind: ColumnKind;
  operator: FilterOperator;
  value: number | boolean | string;
  label: string; // pre-formatted chip text, e.g. "age_ratio ≥ 0.8"
}

export interface NearbyParcelsGeoJSON {
  type: "FeatureCollection";
  features: { type: "Feature"; properties: { id: string }; geometry: NearbyParcel["geometry"] }[];
}

export interface ActiveReferenceLayer {
  name: string;
  opacity: number;
  data: ReferenceLayerGeoJSON;
}

export interface MapViewportBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface GroundwaterDepthRaster {
  image: string; // data:image/png;base64,... — deck.gl BitmapLayer accepts a data URI directly
  bounds: [number, number, number, number]; // [west, south, east, north]
}

/** A session-local, user-edited copy of a package (or a brand-new selection
 * started from a single non-member pipe) — never written back to the real
 * pipeline package_members(_spatial)/packages(_spatial) parquet files.
 * `customId` is null until the first Save (POST /api/custom-packages);
 * after that, Save calls PATCH instead. Once saved, viewing it again goes
 * through the normal package-detail machinery with dataset="custom" (see
 * panel.ts) — this interface only backs the in-progress editing banner. */
export interface EditingPackage {
  customId: string | null;
  name: string;
  waterType: string;
  // When false (default), add/remove on the map is locked to waterType —
  // matches every real pipeline package (DBSCAN clusters strictly within one
  // water_type). When true, pipes of any water_type can be added — mirrors
  // module 09_1's own shared-trench combo packages (water_type="combined"),
  // for building a custom multi-water-type package by hand. Saved water_type
  // is computed at save time from the actual mix of assetIds, not this flag.
  allowMixedWaterTypes: boolean;
  assetIds: string[];
  basedOnPackageId: string | null;
  basedOnDataset: string | null;
}

export type UserRole = "view" | "configure" | "manager" | "admin" | "platform_admin";
export type Team = "strategy" | "packaging" | "delivery" | "asset_data" | "construction" | "operations" | "executive" | "procurement";

export interface CurrentUser {
  username: string;
  display_name: string;
  role: UserRole;
  // null when the admin hasn't assigned this user to a group yet — presence
  // and package sharing are both no-ops for an ungrouped user (no peers).
  group_id: number | null;
  // Which teams/workspaces the user works in (workspaces.ts, backend
  // auth/models.py:TEAMS) — empty = unassigned, falls back to a default.
  teams: Team[];
  // Pipe-count gate for the "create custom package from an area" wizard
  // (areaPackageWizard.ts) — see settings.py's CUSTOM_PACKAGE_WIZARD_MAX_PIPES.
  custom_package_wizard_max_pipes: number;
  // Terms of use (backend terms.py, frontend terms.ts): the current version,
  // whether this user accepted it (every other /api route 403s until they
  // do), and whether this login session has seen the short reminder.
  terms_version: string;
  terms_accepted: boolean;
  terms_session_ack: boolean;
  // Flow plan stage A (routers/auth.py): own id + email (profile dialog).
  user_id?: number;
  email?: string | null;
  // "View as organisation" (backend acting_client.py): client_id is the
  // effective organisation; home_client is the user's own; acting_client is
  // set while a platform admin views as another organisation.
  client_id?: number | null;
  home_client?: ClientRef | null;
  acting_client?: (ClientRef & { allow_edits: boolean; since: string | null }) | null;
  // Presentation / demo mode (backend presentation.py, presentationMask.ts):
  // a per-session display mask; never changes data.
  presentation?: PresentationState;
  // Backend procurement_access.py (WCC_PROCUREMENT_ALLOWED_USERS): false =
  // tender-rate data is restricted to named users and this user is not one;
  // the Procurement workspace, its tabs and the estimator banner are hidden.
  procurement_access?: boolean;
  // Backend onboarding.py: read-only account (every change answers 403) /
  // two-factor sign-in turned on.
  read_only?: boolean;
  mfa_enabled?: boolean;
}

/** May this user reach tender-rate data at all (before role/team rules)?
 * Missing flag (older backend) = no named-user restriction. */
export function hasProcurementAccess(user: CurrentUser | null | undefined): boolean {
  return user?.procurement_access !== false;
}

export interface PresentationState {
  on: boolean;
  mask_staff: boolean;
  allowed: boolean;
}

export interface ClientRef {
  id: number;
  name: string | null;
  slug: string | null;
}

// platform_admin added 2026-09-25 (PROJECT_INTERFACE_VISION.md Phase 1) —
// found missing here via an actual browser check during Phase 2: without
// this entry, ROLE_RANK["platform_admin"] was undefined, so every
// roleAtLeast() check silently failed for that role (undefined >= N is
// always false in JS) even though the backend correctly granted it every
// permission admin has, plus more. Type-checking alone never caught this —
// UserRole was a plain string union, so nothing enforced this record
// having every one of its members.
const ROLE_RANK: Record<UserRole, number> = { view: 0, configure: 1, manager: 2, admin: 3, platform_admin: 4 };

/** True if the current user (or a role passed directly) meets `minimum` —
 * mirrors the backend's auth/models.py:role_at_least exactly, so a frontend
 * gate and its matching backend gate never disagree about ordering. */
export function roleAtLeast(user: CurrentUser | UserRole | null, minimum: UserRole): boolean {
  if (user === null) return false;
  const role = typeof user === "string" ? user : user.role;
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export interface AppState {
  // Null until the initial GET /api/auth/me resolves (see auth.ts) — every
  // other piece of app bootstrap is gated behind this being non-null.
  currentUser: CurrentUser | null;

  datasetName: string | null;
  datasetModule: string | null; // e.g. "07" — the module that produced datasetName, drives the Module config tab
  colorCol: string | null;
  waterTypes: WaterType[];

  geometry: GeometryResponse | null;
  pipes: PipesResponse | null;
  legend: Legend | null;

  // Last-fetched colourable-columns catalog for the current dataset — set
  // by sidebar.ts:refreshColumns alongside its own colour-by picker, reused
  // by columnFilterUI.ts's "add filter" column dropdown so it doesn't need
  // its own separate fetch of the same list.
  columns: ColumnInfo[];
  columnFilters: ColumnFilter[];
  // column -> (asset_id -> value), fetched lazily the first time a filter
  // on that column is added (columnFilterUI.ts) and reused by every filter
  // on the same column after that. Cleared on dataset switch (sidebar.ts).
  columnFilterValues: Map<string, Map<string, number | boolean | string | null>>;

  selectedAssetId: string | null;

  // Packages (module 09 DBSCAN, or module 09_1 spatial-first — see
  // PACKAGE_DATASET_NAMES; the dataset dropdown itself IS the "choose
  // clustering method" control, no separate toggle needed)
  showPkgOverlay: boolean; // lightened colour shade for a top-50/recommended-cut package
  // Independent per-category visibility — replaces the old single
  // "only show pipes in a package" toggle so a pipe not in any package, a
  // pipe in a package outside the recommended cut, and a pipe in the
  // recommended cut can each be shown/hidden on their own (helps tell
  // overlapping packages in one area apart, and isolates the top-50 cut).
  showNonPackagePipes: boolean;
  showOtherPackagePipes: boolean; // in a package, not in the recommended cut
  showRecommendedPipes: boolean; // in the recommended cut
  showPkgHulls: boolean;
  packageBrowse: PackageBrowseRow[];
  packageHulls: HullFeatureCollection | null;
  // Shovel-readiness of EVERY package of the current dataset (readiness.ts,
  // /api/packages/readiness) — badges on any package list, the map's
  // "Colour packages by readiness" and the search results. Null until
  // loaded (or for a dataset without packages / a GIS-view organisation).
  packageReadiness: Record<string, PackageReadiness> | null;
  colorPackagesByReadiness: boolean;
  // The browse table's readiness chips (empty = all) and sort order.
  browseReadinessFilter: string[];
  browseSort: "score" | "readiness" | "pct_ready";
  // Packages the user has explicitly hidden (visibility checkbox in the
  // "packages in view" list, or right-click on a package's pipes/hull) —
  // filters that package's pipes out of the map render in map.ts:joinPipes,
  // same continue-based mechanism as showNonPackagePipes etc. Keyed by real
  // package_id only (custom/committed packages aren't part of this).
  hiddenPackageIds: Set<string>;
  // Custom packages have no package_id tag on their member pipes (that
  // column only ever holds a real module-09/09_1 package_id), so hiding one
  // via hiddenPackageIds can't reuse the same filter real packages use —
  // this is the resolved-to-asset_id form of hiddenPackageIds for custom
  // packages specifically, kept in sync by panel.ts:recomputeHiddenAssetIds
  // (each custom package's own already-loaded asset_ids, no extra fetch).
  // Filtered unconditionally in map.ts:joinPipes, unlike hiddenPackageIds
  // which only applies when a package dataset is active.
  hiddenAssetIds: Set<string>;
  highlightedPackageId: string | null; // magenta
  // A custom package's member pipes don't carry its "CUSTOM-xxxx" id in
  // their own package_id attribute (that column only ever holds a real
  // module-09 package_id) — map.ts's magenta highlight falls back to this
  // explicit asset_id list, pushed by panel.ts once a custom package's
  // detail is fetched, instead of the package_id-column filter used for a
  // real package. Null whenever highlightedPackageId isn't a custom one.
  highlightedPackageAssetIds: string[] | null;
  highlightedAssetId: string | null; // green, within the highlighted package

  // Nearby sub-tab (wcc_viewer.nearby) — the parcels currently listed in the
  // Nearby tab's table, so map.ts can draw their outlines; null whenever
  // that tab isn't the active one (see panel.ts's tab-switch handler).
  nearbyParcels: NearbyParcelsGeoJSON | null;
  highlightedParcelId: string | null;
  // Same tab's "candidate pipes" section — excluded pipes near this
  // package's members, already filtered client-side (exclusion_reason
  // checkboxes, water-type checkbox) by the time they land here. Clicking
  // one reuses highlightedAssetId (the existing single-pipe focus), not a
  // new highlight concept.
  nearbyCandidatePipes: { asset_id: string; path: [number, number][] }[] | null;

  // Reference layers
  referenceLayerCatalog: ReferenceLayerCatalogEntry[];
  activeReferenceLayers: Map<string, ActiveReferenceLayer>;
  // The one raster reference layer (GNS groundwater depth-to-water-table) —
  // handled separately from activeReferenceLayers since it renders via a
  // deck.gl BitmapLayer (an image + bounds), not a GeoJsonLayer.
  showGroundwaterDepthRaster: boolean;
  groundwaterDepthRaster: GroundwaterDepthRaster | null;

  // Current visible map extent (debounced — see map.ts:scheduleViewportUpdate),
  // null until the map has rendered once. Drives the "packages in view" list.
  mapViewport: MapViewportBounds | null;

  // Custom (user-editable) packages — session-local, null unless the user is
  // actively adding/removing pipes (via map right-click, the asset table, or
  // the "Edit as custom package" button). See EditingPackage above.
  editingPackage: EditingPackage | null;
  // While editingPackage is set, map.ts:joinPipes restricts the rendered
  // pipe set to editingPackage.assetIds (plus nearbyCandidatePipes, so the
  // Additional Assets tab still works) unless this is true — clicking a
  // different recommended package elsewhere on the map was disrupting an
  // in-progress edit, so the default is to hide everything else. Reset to
  // false whenever a new edit starts (see the various setState({editingPackage})
  // call sites) so a stale "show everything" choice doesn't leak into the
  // next edit.
  editingShowOtherPipes: boolean;

  // "Create custom package from an area" wizard (areaPackageWizard.ts) —
  // null unless active. Mutually exclusive with editingPackage: entering one
  // clears the other (see areaPackageWizard.ts's own entry point and
  // panel.ts's "Edit as custom package" button).
  areaPackageWizard: AreaPackageWizardState | null;

  // Read-only project scoped view (projectsUI.ts, PROJECT_INTERFACE_VISION.md
  // Phase 2) — null unless a project is open. Same isolation idea as
  // editingPackage (map.ts:joinPipes restricts the rendered pipe set to just
  // these assetIds), but always isolated — there's no "show other pipes"
  // opt-out, since the whole point of a project view is "not have other
  // packages displayed." Never set at the same time as editingPackage;
  // opening one clears the other (see projectsUI.ts/panel.ts's own entry
  // points). sourceType/sourcePackageId/sourceDataset/waterType carried
  // over from ProjectRecord (Phase 3) so the project view can reach the
  // source package without a second round trip back through the Projects
  // list.
  viewingProject: {
    id: string;
    name: string;
    intentionText: string | null;
    assetIds: string[];
    sourceType: "package" | "committed_package" | "external";
    sourcePackageId: string | null;
    sourceDataset: string | null;
    waterType: string | null;
    // Phase 4 — drives whether projectsUI.ts shows "Lock project" (draft/
    // in_design) or "Unlock project" + the task list (locked).
    status: "draft" | "in_design" | "locked" | "in_delivery" | "complete" | "archived";
    // Delivery phase key (wcc_viewer.projects.PHASES) — optional so any
    // older setState caller that doesn't carry it still typechecks;
    // projectsUI.ts treats missing as the first phase.
    phase?: string;
  } | null;

  // Ctrl+click multi-select-and-merge (any sidebar package row, or a pipe/
  // hull on the map) — orange highlight (map.ts), Merge/Cancel context menu
  // (panel.ts). Each entry's assetIds is fetched once at selection time
  // (getPackageDetail), so merging never needs another round trip.
  multiSelectedPackages: MultiSelectedPackage[];
  // One-shot signal: map.ts can't import panel.ts (which owns the actual
  // multi-select/merge logic and already imports map.ts for flyToBounds —
  // importing the other way would be a cycle), so a ctrl+click resolved on
  // the map is handed off via this field instead of a direct function call.
  // panel.ts's own subscribe() consumes it and immediately resets it to null.
  pendingMapPackageMultiSelect: { packageId: string; dataset: string; clientX: number; clientY: number } | null;
  // Indicative traffic-management screening overlay (trafficManagement.ts):
  // pipe asset_id -> TM category, drawn above the pipe layer by map.ts.
  // `forKey` is the package ("pkg:<dataset>:<id>") or project
  // ("proj:<id>") it was computed for — map.ts only draws it while that
  // same package/project is still the one on screen, so switching or
  // closing the view hides it without needing every exit path to clear it.
  trafficManagementOverlay: { forKey: string; categories: Record<string, TrafficCategory> } | null;
  // Replacement-strategy "Show on map" (replacementStrategy.ts): the pipes
  // scheduled for one plan year (optionally one street unit of it), drawn
  // on top by map.ts straight from `geometry` (not the current dataset's
  // joined pipes, so it shows regardless of dataset/filters). One at a time.
  strategyYearOverlay: { year: number; unitId: string | null; label: string; assetIds: Set<string> } | null;
  // Pipes already claimed by a non-archived Project (projectClaims.ts,
  // /api/project-claims) — asset_id -> project ids, plus a project lookup.
  // Outlined on the map while showProjectClaims is on (map.ts), and named
  // in the selected-pipe panel (panel.ts), so nobody packages them twice.
  projectClaims: {
    claims: Map<string, string[]>;
    projects: Map<string, ProjectClaimInfo>;
  } | null;
  showProjectClaims: boolean;
  // A simulation run's map overlay (simulationsUI.ts, /api/simulations/runs/
  // {id}/layers/{key}?mode=…): pre-coloured GeoJSON (each feature carries
  // properties._color) or raster images, drawn on top by map.ts. Hides the
  // package-creation overlays like strategyYearOverlay does.
  simulationOverlay: SimulationOverlay | null;
}

export type SimulationMode = "base" | "scenario" | "diff";

export interface SimulationOverlayLayer {
  key: string;
  label: string;
  geometry: "line" | "point" | "polygon" | "raster";
  field: string | null;
  // vector: a FeatureCollection whose features carry properties._color
  data?: { type: "FeatureCollection"; features: unknown[] };
  // raster: an image URL and its [west, south, east, north] bounds
  image?: string;
  bounds?: [number, number, number, number];
  tooltipFields: string[];
}

export interface SimulationOverlay {
  runId: string;
  mode: SimulationMode;
  layers: SimulationOverlayLayer[];
  // true: map.ts leaves the pipe layer out while this overlay shows (the
  // traffic disruption result; its "Show pipes" toggle turns it off)
  hidePipes?: boolean;
}

export interface ProjectClaimInfo {
  project_id: string;
  name: string;
  status: string;
  phase: string | null;
  phase_label: string | null;
  n_assets: number;
  health?: "green" | "amber" | "red" | null;
}

export type TrafficCategory = "none" | "low" | "medium" | "high";

/** Step "draw": instruction UI + ctrl-drag/"use whole view" pipe pickup
 * (see map.ts:pickPipesInRect). Step "configure": the mini-config tabs +
 * live preview, once candidateAssetIds has been gated under
 * currentUser.custom_package_wizard_max_pipes. */
export interface AreaPackageWizardState {
  step: "draw" | "configure";
  candidateAssetIds: string[];
  config: AreaPackageMiniConfig;
  preview: AreaPackagePreviewResult | null;
  previewLoading: boolean;
  // Set once the user picks "Update this preset" / "Save as new preset" so
  // the Presets tab knows which record a further "Update" targets; null
  // means the current config isn't (yet) tied to any saved preset.
  loadedPresetId: string | null;
  // Set when this session was started via "Re-tune with area wizard" on an
  // already-saved custom package (panel.ts) rather than from scratch — null
  // for a from-scratch session. Drives the Save section: non-null means
  // "Save changes" (update this record in place, the default) plus a
  // secondary "Save as copy", matching renderEditingView's own save-as-copy
  // convention; null means the single "Save package" (always creates new).
  basedOnCustomPackageId: string | null;
  // Set when this session was started from the WSS viewer tab (wssViewer.ts)
  // by clicking a row — null otherwise. Drives whether the wizard panel
  // shows a "Commit to WSS" button alongside the normal Save button.
  sourceWssItemId: string | null;
  // Set when this session works for a work brief ("Start analysis", or a
  // re-tune of a package built for one): the candidate pool is the brief's
  // frozen pipes and Save records the brief on the custom package, so a later
  // re-tune keeps the brief's area and policies (2026-10-09 showcase fix).
  briefId?: string | null;
}

/** Seed values for a brand-new wizard session — mirrors config/weights.
 * yaml's `default` preset and config/package_params.yaml's
 * `package_value_nzd` bounds, the same global defaults module 07/09 use,
 * so "no overrides yet" behaves the same as the real pipeline until the
 * user actually moves something. */
export const DEFAULT_AREA_PACKAGE_MINI_CONFIG: AreaPackageMiniConfig = {
  weights: { age: 0.2, condition: 0.25, urgency: 0.15, criticality: 0.2, operational: 0.2 },
  criticalityGoals: null,
  leakWeight: 0,
  complexityOverrides: {},
  allowMixedWaterTypes: false,
  costTargetNzd: { min: 500000, target: 4000000, max: 3000000 },
};

export interface MultiSelectedPackage {
  packageId: string;
  dataset: string;
  name: string;
  assetIds: string[];
}

type Listener = (state: AppState) => void;

const state: AppState = {
  currentUser: null,
  datasetName: null,
  datasetModule: null,
  colorCol: null,
  waterTypes: ["water", "wastewater", "stormwater"],
  geometry: null,
  pipes: null,
  legend: null,
  columns: [],
  columnFilters: [],
  columnFilterValues: new Map(),
  selectedAssetId: null,
  showPkgOverlay: true,
  showNonPackagePipes: true,
  showOtherPackagePipes: true,
  showRecommendedPipes: true,
  showPkgHulls: true,
  packageBrowse: [],
  packageHulls: null,
  packageReadiness: null,
  colorPackagesByReadiness: false,
  browseReadinessFilter: [],
  browseSort: "score",
  hiddenPackageIds: new Set(),
  hiddenAssetIds: new Set(),
  highlightedPackageId: null,
  highlightedPackageAssetIds: null,
  highlightedAssetId: null,
  nearbyParcels: null,
  highlightedParcelId: null,
  nearbyCandidatePipes: null,
  referenceLayerCatalog: [],
  activeReferenceLayers: new Map(),
  showGroundwaterDepthRaster: false,
  groundwaterDepthRaster: null,
  mapViewport: null,
  editingPackage: null,
  editingShowOtherPipes: false,
  areaPackageWizard: null,
  viewingProject: null,
  multiSelectedPackages: [],
  pendingMapPackageMultiSelect: null,
  trafficManagementOverlay: null,
  strategyYearOverlay: null,
  projectClaims: null,
  showProjectClaims: true,
  simulationOverlay: null,
};

const listeners = new Set<Listener>();

export function getState(): Readonly<AppState> {
  return state;
}

export function setState(patch: Partial<AppState>): void {
  Object.assign(state, patch);
  for (const fn of listeners) fn(state);
}

/** Subscribe to every state change. Returns an unsubscribe function. Phase 1
 * has few enough widgets that a single "re-render everything relevant" fan-out
 * is simpler and more robust than fine-grained per-key subscriptions — this
 * can be split into topic-scoped subscriptions later if it gets unwieldy. */
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const viewportListeners = new Set<() => void>();

/** mapViewport gets its own tiny, separate notification path instead of
 * going through setState/listeners above — it updates on every map pan/
 * zoom (even debounced, still far more often than anything else touches
 * state), and broadcasting it through the general fan-out meant EVERY
 * subscriber re-ran on every pan, including map.ts's own render() —
 * rebuilding the entire pipe layer (joinPipes over the whole dataset) for a
 * value render() doesn't even use, since nothing about pipe rendering
 * depends on the visible viewport. Confirmed as the source of client-
 * reported lag while panning. Only panel.ts's "packages in view" list
 * actually needs to know when this changes — see subscribeViewport. */
export function setMapViewport(bounds: MapViewportBounds): void {
  state.mapViewport = bounds;
  for (const fn of viewportListeners) fn();
}

export function subscribeViewport(fn: () => void): () => void {
  viewportListeners.add(fn);
  return () => viewportListeners.delete(fn);
}

const packageMultiSelectListeners = new Set<() => void>();

/** Same rationale as setMapViewport/subscribeViewport above — ctrl+clicking
 * a package row to add it to the multi-select-and-merge set was going
 * through plain setState, which fans out to every subscriber including
 * panel.ts's own "re-render the whole Details tab" listener. That tore down
 * and rebuilt the entire sidebar list on every single ctrl+click (losing
 * scroll position mid-selection), making it near-impossible to actually
 * select more than one package from a scrolled list. Only map.ts's render()
 * (for the orange highlight) and panel.ts's own toggle/menu logic need to
 * react to this — nothing about the sidebar's list *rendering* does, since
 * ctrl+click deliberately never touches highlightedPackageId. */
export function setPackageMultiSelectState(
  patch: Partial<Pick<AppState, "multiSelectedPackages" | "pendingMapPackageMultiSelect">>,
): void {
  Object.assign(state, patch);
  for (const fn of packageMultiSelectListeners) fn();
}

export function subscribePackageMultiSelect(fn: () => void): () => void {
  packageMultiSelectListeners.add(fn);
  return () => packageMultiSelectListeners.delete(fn);
}

/** Every dataset the packages UI (browse table, hulls, highlighting, hide-
 * non-package filter) applies to: the two raw clustering outputs (DBSCAN
 * "package_members", spatial-first "package_members_spatial" — module 09_1)
 * plus "pipe_display" — the viewer-only join of pipe_scores_final +
 * pipe_leakage_risk with package_id attached from 09_1 (see backend
 * virtual_datasets.py). Packages are only ever relevant when one of these
 * IS the currently selected dataset — a package_members.parquet that exists
 * on disk doesn't mean it's *current* relative to whatever upstream module
 * the user just edited, so it must never leak into another module's view.
 * The dataset dropdown itself is the "choose DBSCAN vs spatial" control (see
 * sidebar.ts) — no separate toggle.
 *
 * Note "only show pipes in a package" is a no-op on package_members(_spatial)
 * themselves — every row there already has a package_id by construction. It
 * only actually filters anything on pipe_display, which also carries
 * FWP/NOE/immaturity-excluded pipes that never made it into a package. */
export const PACKAGE_DATASET_NAMES = ["package_members", "package_members_spatial", "pipe_display"] as const;

export function packagesActive(state: Pick<AppState, "datasetName">): boolean {
  return (PACKAGE_DATASET_NAMES as readonly (string | null)[]).includes(state.datasetName);
}
